import { useState, useEffect, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'
import { useAuth } from '@context/AuthContext'
import PlanesManager from './components/PlanesManager'
import DatosManager from './components/DatosManager'
import ResultadoPanel from './components/ResultadoPanel'
import KpiPanel from './components/KpiPanel'
import styles from './Comisiones.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL

export async function authHeaders() {
    const session = await fetchAuthSession()
    return { Authorization: `Bearer ${session.tokens.idToken.toString()}` }
}

const PERIODOS_SUGERIDOS = () => {
    const now = new Date()
    const year = now.getFullYear()
    const options = []
    for (let q = 1; q <= 4; q++) options.push(`Q${q}-${year}`)
    for (let m = 1; m <= 12; m++) options.push(`${String(m).padStart(2,'0')}-${year}`)
    return options
}

function Comisiones() {
    const { userEmpresa, userEmail, isAdmin, isSuperAdmin } = useAuth()
    const canManage = isAdmin || isSuperAdmin

    const [activeTab, setActiveTab] = useState(canManage ? 'planes' : 'mis_datos')
    const [periodo, setPeriodo] = useState(`Q${Math.ceil((new Date().getMonth()+1)/3)}-${new Date().getFullYear()}`)
    const [planes, setPlanes] = useState([])
    const [datos, setDatos] = useState([])
    const [loading, setLoading] = useState(true)
    const [msg, setMsg] = useState(null)

    const empresaId = userEmpresa

    const loadAll = useCallback(async () => {
        if (!empresaId || empresaId === 'Sin empresa') return
        setLoading(true)
        try {
            const headers = await authHeaders()
            const [planesRes, datosRes] = await Promise.all([
                axios.get(`${API}/comisiones/${empresaId}/planes?periodo=${periodo}`, { headers }),
                axios.get(`${API}/comisiones/${empresaId}/datos?periodo=${periodo}`, { headers }),
            ])
            setPlanes(planesRes.data.planes || [])
            setDatos(datosRes.data.datos || [])
        } catch (err) {
            setMsg({ type: 'error', text: 'Error cargando comisiones: ' + (err.response?.data?.error || err.message) })
        } finally {
            setLoading(false)
        }
    }, [empresaId, periodo])

    useEffect(() => { loadAll() }, [loadAll])

    // Stats para el header
    const totalAprobados = datos.filter(d => d.estado === 'aprobado').length
    const totalEnviados  = datos.filter(d => d.estado === 'enviado').length
    const totalBorradores = datos.filter(d => d.estado === 'borrador').length
    const totalComision  = datos.filter(d => d.estado === 'aprobado')
        .reduce((acc, d) => acc + (d.comision_calculada?.total_cop || 0), 0)

    const TABS = [
        ...(canManage ? [
            { id: 'planes',   label: '📋 Planes / Reglas', desc: 'Configura reglas de comisión por gestor' },
            { id: 'datos',    label: '📊 Rendimiento',      desc: 'Ingresa y aprueba datos de ventas' },
            { id: 'resumen',  label: '💰 Resumen',          desc: 'Vista consolidada de comisiones' },
            { id: 'kpi',      label: 'KPI',                 desc: 'Tablero ejecutivo de indicadores' },
        ] : [
            { id: 'mis_datos', label: '📊 Mis Datos', desc: 'Ingresa tus datos de rendimiento' },
            { id: 'mi_plan',   label: '📋 Mi Plan',   desc: 'Visualiza tus reglas de comisión' },
        ])
    ]

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerLeft}>
                        <div className={styles.headerIcon}>💰</div>
                        <div>
                            <h1 className={styles.title}>Gestor de Comisiones</h1>
                            <p className={styles.subtitle}>
                                {canManage
                                    ? 'Gestiona planes, valida datos y calcula comisiones del equipo comercial'
                                    : 'Registra tu rendimiento y consulta tu comisión'}
                            </p>
                        </div>
                    </div>

                    {/* Selector de período */}
                    <div className={styles.periodoSelector}>
                        <label>Período</label>
                        <select
                            value={periodo}
                            onChange={e => { setPeriodo(e.target.value); setMsg(null) }}
                            className={styles.periodoSelect}
                            id="periodo-select"
                        >
                            {PERIODOS_SUGERIDOS().map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Stats row */}
                {canManage && (
                    <div className={styles.statsRow}>
                        <div className={styles.statCard}>
                            <span className={styles.statNum}>{planes.length}</span>
                            <span className={styles.statLabel}>Planes activos</span>
                        </div>
                        <div className={`${styles.statCard} ${styles.statAprobado}`}>
                            <span className={styles.statNum}>{totalAprobados}</span>
                            <span className={styles.statLabel}>Aprobados</span>
                        </div>
                        <div className={`${styles.statCard} ${styles.statPendiente}`}>
                            <span className={styles.statNum}>{totalEnviados}</span>
                            <span className={styles.statLabel}>Pendientes revisión</span>
                        </div>
                        <div className={`${styles.statCard} ${styles.statBorrador}`}>
                            <span className={styles.statNum}>{totalBorradores}</span>
                            <span className={styles.statLabel}>En borrador</span>
                        </div>
                        <div className={`${styles.statCard} ${styles.statTotal}`}>
                            <span className={styles.statNum}>
                                ${totalComision.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
                            </span>
                            <span className={styles.statLabel}>Total comisiones aprobadas</span>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Tabs ── */}
            <div className={styles.tabsBar}>
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        id={`tab-${tab.id}`}
                        className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                        onClick={() => { setActiveTab(tab.id); setMsg(null) }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Alertas ── */}
            {msg && (
                <div className={`${styles.alert} ${styles[`alert${msg.type.charAt(0).toUpperCase() + msg.type.slice(1)}`]}`}>
                    {msg.text}
                    <button className={styles.alertClose} onClick={() => setMsg(null)}>✕</button>
                </div>
            )}

            {/* ── Contenido ── */}
            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loadingBlock}>
                        <div className={styles.spinner}></div>
                        <span>Cargando datos...</span>
                    </div>
                ) : empresaId === 'Sin empresa' ? (
                    <div className={styles.emptyState}>
                        <span>🏢</span>
                        <h3>Sin empresa asignada</h3>
                        <p>Tu usuario aún no tiene empresa asignada. Contacta al administrador.</p>
                    </div>
                ) : (
                    <>
                        {/* Admin tabs */}
                        {activeTab === 'planes' && canManage && (
                            <PlanesManager
                                empresaId={empresaId}
                                periodo={periodo}
                                planes={planes}
                                onRefresh={loadAll}
                                setMsg={setMsg}
                            />
                        )}
                        {activeTab === 'datos' && canManage && (
                            <DatosManager
                                empresaId={empresaId}
                                periodo={periodo}
                                planes={planes}
                                datos={datos}
                                onRefresh={loadAll}
                                setMsg={setMsg}
                                isAdmin={canManage}
                                userEmail={userEmail}
                            />
                        )}
                        {activeTab === 'resumen' && canManage && (
                            <ResultadoPanel
                                planes={planes}
                                datos={datos}
                                periodo={periodo}
                            />
                        )}
                        {activeTab === 'kpi' && canManage && (
                            <KpiPanel
                                planes={planes}
                                datos={datos}
                                periodo={periodo}
                            />
                        )}
                        {/* User tabs */}
                        {activeTab === 'mis_datos' && !canManage && (
                            <DatosManager
                                empresaId={empresaId}
                                periodo={periodo}
                                planes={planes.filter(p => p.gestor_email === userEmail)}
                                datos={datos.filter(d => d.gestor_email === userEmail)}
                                onRefresh={loadAll}
                                setMsg={setMsg}
                                isAdmin={false}
                                userEmail={userEmail}
                                soloPropio={true}
                            />
                        )}
                        {activeTab === 'mi_plan' && !canManage && (
                            <PlanesManager
                                empresaId={empresaId}
                                periodo={periodo}
                                planes={planes.filter(p => p.gestor_email === userEmail)}
                                onRefresh={loadAll}
                                setMsg={setMsg}
                                readOnly={true}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default Comisiones
